import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { vendorTenantsApi, vendorAuthApi } from '@/lib/vendorApi';
import type { VendorTenant } from '@/lib/vendorApi';
import { StatsStrip }   from '@/components/vendor/StatsStrip';
import { TenantTable }  from '@/components/vendor/TenantTable';
import type { Action }  from '@/components/vendor/TenantTable';
import { TenantDrawer } from '@/components/vendor/TenantDrawer';

export function VendorDashboard() {
  const navigate     = useNavigate();
  const qc           = useQueryClient();
  const [activeTenant, setActiveTenant] = useState<VendorTenant | null>(null);
  const [activeAction, setActiveAction] = useState<Action | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-tenants'],
    queryFn:  vendorTenantsApi.list,
    retry:    false,
  });

  // Redirect to login if unauthorized
  if (error) {
    navigate('/vendor/login');
    return null;
  }

  function openDrawer(tenant: VendorTenant, action: Action) {
    setActiveTenant(tenant);
    setActiveAction(action);
  }

  function closeDrawer() {
    setActiveTenant(null);
    setActiveAction(null);
  }

  async function handleLogout() {
    await vendorAuthApi.logout();
    navigate('/vendor/login');
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* Top bar */}
      <div className="border-b border-slate-800 px-8 py-4 flex items-center justify-between">
        <div>
          <span className="text-brand-400 font-bold text-sm tracking-wide uppercase">VantixMT</span>
          <span className="text-slate-500 text-sm ml-2">Vendor Admin</span>
        </div>
        <button
          onClick={handleLogout}
          className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
        >
          Sign out
        </button>
      </div>

      <div className="px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-100 mb-6">Tenants</h1>

        {isLoading ? (
          <div className="text-slate-500 text-sm">Loading...</div>
        ) : data ? (
          <>
            <StatsStrip stats={data.stats} />
            <TenantTable tenants={data.tenants} onAction={openDrawer} />
          </>
        ) : null}
      </div>

      <TenantDrawer
        key={`${activeTenant?.id}-${activeAction}`}
        tenant={activeTenant}
        action={activeAction}
        onClose={closeDrawer}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['vendor-tenants'] });
        }}
      />
    </div>
  );
}
